import { message } from "antd";
import apiRequest from "./apiRequest";

type Action = "add" | "edit" | "delete" | "view" | "view_own";
type PermissionKey = string;
type PermissionStore = Record<PermissionKey, boolean>;

const LOCAL_STORAGE_KEY = "erp_permission_cache";

const getKey = (module: string, slug: string, action: Action): PermissionKey =>
  `${module}_${slug}_${action}`;

export const loadPermissions = (): void => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch (e) {
    console.error("Failed to load permissions from localStorage", e);
  }
};

const savePermissions = (store: PermissionStore): void => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(store));
};

const getPermissionStore = (): PermissionStore => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const hasAnyPermssionChagedWithPrevious = (
  newAllowed = [],
  newDenied = []
): boolean => {
  const store = getPermissionStore();

  if (Array.isArray(newAllowed) && newAllowed.length > 0) {
    for (const { module, slug, action } of newAllowed) {
      if (store[getKey(module, slug, action)] !== true) return true;
    }
  }

  if (Array.isArray(newDenied) && newDenied.length > 0) {
    for (const { module, slug, action } of newDenied) {
      if (store[getKey(module, slug, action)] !== false) return true;
    }
  }

  return false;
};

export const syncPermissions = async (): Promise<void> => {
  const API_REQUEST = new apiRequest("permissions/get", true);
  const response = await API_REQUEST.sendAndCache("permissions", 10000);

  if (
    response.status === "success" &&
    (response.data.allowed.length > 0 || response.data.denied.length > 0)
  ) {
    try {
      const store: PermissionStore = getPermissionStore();

      if (response.data.allowed.length > 0) {
        for (const { module, slug, action } of response.data.allowed) {
          store[getKey(module, slug, action)] = true;
        }
      }

      if (response.data.denied.length > 0) {
        for (const { module, slug, action } of response.data.denied) {
          store[getKey(module, slug, action)] = false;
        }
      }

      if (
        hasAnyPermssionChagedWithPrevious(
          response.data.allowed,
          response.data.denied
        )
      ) {
        if (localStorage.getItem("menu_cache"))
          localStorage.removeItem("menu_cache");
        message.info("Your permissions have changed. Please refresh the page.");
      }

      savePermissions(store);
    } catch (e) {
      console.error("Failed to sync permissions", e);
    }
  }
};

const has_permission = (
  module: string,
  slug: string,
  action: Action | Action[]
): boolean => {
  const store = getPermissionStore();

  const act = module == "global" ? slug : module + "_" + slug;

  if (Array.isArray(action)) {
    for (const a of action) {
      if (store[getKey(module, act, a)] === true) {
        return true;
      }
    }

    return false;
  } else {
    return store[getKey(module, act, action)] === true;
  }
};

export default has_permission;
